import { ENTITIES } from 'lib/constants';

/**
 * Copilot service constants and configuration
 *
 * This module defines all constants, enums, and schemas used by the Copilot service,
 * including modalities, features, session types, and interaction types.
 *
 * @module CopilotConstants
 */

/**
 * Available modalities for the copilot service
 * @readonly
 * @enum {string}
 */
export const MODALITIES = {
    /** Text-based interactions */
    TEXT: 'text',
    /** View/visual content interactions */
    VIEW: 'view',
};

/**
 * Available features aka "agents" that can be enabled for the copilot service
 * @readonly
 * @enum {string}
 */
export const FEATURES = {
    /** Report generation and analysis */
    REPORTS: 'reports',
    /** Vendor synchronization analysis */
    VENDOR_SYNC_ANALYZE: 'vendor_sync_analyze',
    /** Entity-specific chat functionality */
    ENTITY_CHAT: 'entity_chat',
    /** Knowledge base integration */
    KNOWLEDGE_BASE: 'knowledge-base',
    /** Sage copilot integration */
    SAGE_COPILOT: 'sales_copilot',
    SAGE_COPILOT_CRUD: 'sage_copilot_crud',
    /** Entity summary for GoldenMinute */
    ENTITY_SUMMARY: 'entity_summary',
    /** Prompt entity (p.e. email activities summarization) */
    PROMPT_ENTITY: 'prompt_entity',
    /** Related entities analysis (p.e. analyze activities related to the current entity from the activities widget) */
    ACTIVITIES_ANALYZE: 'activity_analyze',
    OPPORTUNITIES_ANALYZE: 'opportunities_analyze',
    SALES_ORDERS_ANALYZE: 'sales_orders_analyze',
    /** Business intelligence */
    BUSINESS_INTELLIGENCE: 'business_intelligence',
};

/**
 * Function name constants for copilot service tools
 * @readonly
 * @enum {string}
 */
export const COPILOT_FUNCTION_NAMES = {
    /** Get the initial form that the user sees to complete */
    GET_INITIAL_FORM: 'get_initial_form',
    /** Get the current dashboard's ID */
    GET_CURRENT_DASHBOARD: 'get_current_dashboard',
};

/**
 * Feature (aka "agent") configuration define available props, tools and their parameters for each feature
 * @readonly
 * @type {Object.<string, Object>}
 */
export const FEATURES_CONFIG = {
    [FEATURES.SAGE_COPILOT_CRUD]: {
        tools: [
            {
                name: COPILOT_FUNCTION_NAMES.GET_INITIAL_FORM,
                description: 'Gets the initial form that the user sees to complete',
                properties: {
                    type: 'object',
                    properties: {
                        entity: {
                            type: 'string',
                            description: 'Entity form',
                            enum: [
                                ENTITIES.ACCOUNTS.name,
                                ENTITIES.ACTIVITIES.name,
                                ENTITIES.CONTACTS.name,
                                ENTITIES.OPPORTUNITIES.name,
                                ENTITIES.SALESORDERS.name,
                                ENTITIES.CALENDAR.name,
                                ENTITIES.TASKS.name,
                            ],
                        },
                    },
                    required: ['entity'],
                },
            },
        ],
    },
    [FEATURES.BUSINESS_INTELLIGENCE]: {
        tools: [
            {
                name: COPILOT_FUNCTION_NAMES.GET_CURRENT_DASHBOARD,
                description:
                    'Notifies the client application to return the app dashboard ID of the dashboard the user is currently viewing. If there is no active dashboard, the app opens the dashboard view. Use when the user wants to analyze or view the dashboard they have open.',
                properties: {
                    type: 'object',
                    properties: {
                        show: {
                            type: 'boolean',
                        },
                    },
                    required: ['show'],
                },
            },
        ],
    },
};

/**
 * Available session types for the copilot service
 * @readonly
 * @enum {string}
 */
export const SESSION_TYPES = {
    /** Create a new session */
    CREATE: 'session.create',
    /** Update an existing session */
    UPDATE: 'session.update',
};

/**
 * Available interaction types for the copilot service
 * @readonly
 * @enum {string}
 */
export const INTERACTION_TYPES = {
    /** Standard message interaction */
    MESSAGE: 'message',
    /** Parameter-based interaction */
    PARAMS: 'params',
    /** Function call output interaction */
    FUNCTION_CALL_OUTPUT: 'function_call_output',
    /** Form interaction */
    FORM: 'form',
};

/**
 * Content types for interaction messages
 * @readonly
 * @enum {string}
 */
export const CONTENT_TYPES = {
    /** Text input content */
    INPUT_TEXT: 'input_text',
    /** File input content (URL or S3 key) */
    INPUT_FILE: 'input_file',
};

/**
 * Available interaction item types for the copilot service
 *
 * For voice interactions, send one INPUT_AUDIO_BUFFER_APPEND event per audio batch,
 * followed by a single INPUT_AUDIO_BUFFER_COMMIT event with the same event_id.
 *
 * @readonly
 * @enum {string}
 */
export const INTERACTION_ITEM_TYPES = {
    /** Create a conversation item */
    CREATE: 'conversation.item.create',
    /** Append audio batch to the input audio buffer for voice interactions */
    INPUT_AUDIO_BUFFER_APPEND: 'input_audio_buffer.append',
    /** Commit the accumulated audio buffer data to process the voice input */
    INPUT_AUDIO_BUFFER_COMMIT: 'input_audio_buffer.commit',
};

/**
 * Response type constants for processing streaming events
 * @readonly
 * @enum {string}
 */
export const RESPONSE_TYPE = {
    /** Session updated event */
    SESSION_UPDATED: 'session.updated',
    /** Text delta response */
    TEXT_TYPE: 'response.text.delta',
    /** Content part done response */
    DATA_TYPE: 'response.content_part.done',
    /** Content part added response */
    PROGRESS_TYPE: 'response.content_part.added',
    /** Progress sub-type */
    PROGRESS_SUB_TYPE: 'progress',
    /** Error response */
    ERROR_TYPE: 'response.error',
    /** View sub-type */
    VIEW_SUB_TYPE: 'view',
    /** Action done response */
    ACTION_DONE: 'response.action.done',
    /** Form done response */
    FORM_DONE: 'response.form.done',
    /** Artifact created response */
    ARTIFACT_CREATED: 'response.artifact.created',
    /** Response done event (final summary including artifacts, forms, actions, output) */
    RESPONSE_DONE: 'response.done',
    /** Function call response */
    FUNCTION_CALL: 'function_call',
    /** Form progress response */
    FORM_PROGRESS_TYPE: 'response.form.progress',
    /** Tool execution initialized response */
    TOOL_INIT_TYPE: 'response.tool.init',
    /** Tool execution completed response */
    TOOL_DONE_TYPE: 'response.tool.done',
    /** Text segment completed response */
    TEXT_DONE_TYPE: 'response.text.done',
};

/**
 * End of response marker indicating stream completion
 * @readonly
 * @type {string}
 */
export const END_RESPONSE = '[DONE]';

/**
 * Discriminator for the new resource-style artifacts (`dashboard`, `markdown`,
 * `form_entity`, …) delivered via `response.artifact.created`. Distinguishes
 * them from legacy artifacts (`generate_chart`, `generate_table`).
 *
 * @readonly
 * @type {string}
 */
export const ARTIFACT_RESOURCE_TYPE = 'artifact_resource';

/**
 * Catalogue of known `artifactType` values for resource artifacts. Used as a
 * convenience reference; new types are accepted at runtime via the registered
 * artifact handlers (with a default fallback).
 *
 * @readonly
 * @enum {string}
 */
export const ARTIFACT_TYPES = {
    DASHBOARD: 'dashboard',
    MARKDOWN: 'markdown',
    FORM_ENTITY: 'form_entity',
};

/**
 * @typedef {Object} CopilotArtifactDownload
 * @property {string} bucket - Storage bucket where the artifact lives.
 * @property {string} name - File name inside the bucket.
 */

/**
 * @typedef {Object} CopilotArtifact
 * @property {string} type - Artifact schema discriminator (e.g. `artifact_resource`).
 * @property {string} artifactType - Specific artifact type (`dashboard`, `markdown`, `form_entity`, …).
 * @property {string} name - Human-readable file name.
 * @property {string} mimeType - MIME type of the artifact payload.
 * @property {Object} [props] - Optional structured properties for the artifact (varies per `artifactType`).
 * @property {CopilotArtifactDownload} download - Storage location for the artifact payload.
 */
